import { useState, useEffect } from 'react';
import { crmService } from '../../services/crmService';
import { useIsHQ } from '../../hooks/useIsHQ';

const todayKey = () => `elephant_welcome_${new Date().toLocaleDateString('en-CA')}`;

export const shouldShowWelcome = () => {
  try { return localStorage.getItem(todayKey()) !== '1'; } catch { return false; }
};

const formatM = (v) => {
  if (!v) return '$0';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const Mini = ({ label, value, sub, accent = '#22D3EE' }) => (
  <div style={{ background: '#0A0F1C', border: '1px solid #1E2D45', borderTop: `2px solid ${accent}`, borderRadius: 6, padding: '10px 14px' }}>
    <div style={{ color: '#6B7280', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{label}</div>
    <div style={{ color: '#F1F5F9', fontSize: 20, fontWeight: 700, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ color: '#4B5563', fontSize: 10, marginTop: 3 }}>{sub}</div>}
  </div>
);

export default function WelcomeSnapshot({ onClose }) {
  const isHQ = useIsHQ();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    crmService.getDashboard().then(setData).catch(() => setFailed(true));
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(todayKey(), '1'); } catch { /* private mode */ }
    onClose();
  };

  // If the snapshot can't load, don't block the morning — skip silently
  useEffect(() => { if (failed) onClose(); }, [failed, onClose]);

  if (failed) return null;

  const totalPipeline = data ? Object.entries(data.pipeline || {})
    .filter(([stage]) => !['won', 'lost'].includes(stage))
    .reduce((sum, [, d]) => sum + (d.value || 0), 0) : 0;
  const overdue = data?.overdue_actions || [];
  const upcoming = data?.upcoming_actions || [];
  const cov = data?.coverage || { white_space: 0, total: 12 };
  const fw = data?.funding_watch || { watching: 0, added_7d: 0 };
  const attention = [...overdue.map(a => ({ ...a, overdue: true })), ...upcoming].slice(0, 4);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(4,8,18,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{
        background: '#111827', border: '1px solid #22D3EE33', borderRadius: 12, width: 560, maxWidth: '92vw',
        maxHeight: '88vh', overflowY: 'auto', padding: '28px 30px', color: '#F1F5F9',
        boxShadow: '0 0 60px rgba(34,211,238,0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
          <img src="/elephant-icon.svg" alt="" style={{ width: 44, height: 44 }} />
          <div>
            <div style={{ fontSize: 21, fontWeight: 700, color: '#F1F5F9' }}>{greeting()}.</div>
            <div style={{ fontSize: 11, color: '#6B7280' }}>
              {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })} — here's the state of the herd.
            </div>
          </div>
        </div>

        {!data ? (
          <div style={{ color: '#94A3B8', textAlign: 'center', padding: 30, fontSize: 12 }}>Gathering the herd…</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 8, marginTop: 16 }}>
              <Mini label="Active Pipeline" value={formatM(totalPipeline)} sub="pre-award pursuits" />
              <Mini
                label="Actions Due" value={upcoming.length + overdue.length}
                sub={overdue.length > 0 ? `${overdue.length} overdue` : 'nothing overdue'}
                accent={overdue.length > 0 ? '#EF4444' : '#10B981'}
              />
              <Mini
                label="Technology Gap" value={`${cov.white_space} of ${cov.total}`}
                sub="verticals in white space" accent="#c4a563"
              />
              {isHQ ? (
                <Mini label="Deal Flow" value={fw.watching} sub={fw.added_7d > 0 ? `+${fw.added_7d} this week` : 'companies watched'} />
              ) : (
                <Mini label="Interactions (7d)" value={data.stats.interactions_7d ?? 0} sub="logged this week" />
              )}
            </div>

            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Needs attention</div>
              {attention.length === 0 ? (
                <div style={{ fontSize: 12, color: '#4B5563' }}>No follow-ups on the calendar — the savanna is quiet.</div>
              ) : attention.map(a => (
                <div key={`${a.overdue ? 'o' : 'u'}-${a.id}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 0' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: a.overdue ? '#EF4444' : '#22D3EE', flexShrink: 0, alignSelf: 'center' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{a.first_name} {a.last_name}</span>
                  <span style={{ fontSize: 11, color: '#6B7280', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.next_action}</span>
                  <span style={{ fontSize: 10, color: a.overdue ? '#EF4444' : '#22D3EE' }}>{new Date(a.next_action_date).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <button onClick={dismiss} style={{
          marginTop: 20, width: '100%', padding: '10px 0', background: '#22D3EE', color: '#0A0F1C',
          border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 13,
        }}>
          Enter the Watering Hole
        </button>
        <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11, color: '#374151', fontStyle: 'normal' }}>
          Toujours en avant.
        </div>
      </div>
    </div>
  );
}
